/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

import ScreenFrameBuffer from "App/render/webgl/utils/screen_frame_buffer";
import {Framebuffer} from "App/utils/frame_buffer";
import WebGLContext from "App/web_gl_context";
import MemoryManagement from "App/memory_management";
import {logWarning} from "App/debug";

/**
 * Framebuffer with multiple color textures and zero or single render buffer
 *
 * @param aFramebufferUtil
 * @class
 */
export default
class MRTFrameBuffer extends ScreenFrameBuffer {
    private _renderBuffer: WebGLRenderbuffer;
    private _colorTexture: WebGLTexture[];

    constructor(aFramebufferUtil: Framebuffer) {
        super(aFramebufferUtil);

        this._renderBuffer = null;
        this._colorTexture = [];
    }

    /**
     * @param aNumber
     * @returns {WebGLTexture}
     */
    public getColorTexture(aNumber: number) {
        let num = (aNumber === undefined || aNumber < 0) ? 0 : aNumber;
        return this._colorTexture[num];
    }

    /**
     * @override
     */
    public bind() {
        let gl = this._context.getWebGLRenderingContext();

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboID);

        if (WebGLContext.isWebGL2) {
            // MRT Extension
            let buffers = [];
            for (let i = 0; i < this._colorTexture.length; ++i) {
                buffers.push(gl["COLOR_ATTACHMENT" + i]); // gl_FragData[i]
            }
            // @ts-ignore
            gl.drawBuffers(buffers);
        } else {
            let ext = WebGLContext.getFeatureAsExtension("DRAW_BUFFERS");
            // MRT Extension
            let buffers = [];
            for (let i = 0; i < this._colorTexture.length; ++i) {
                buffers.push(ext["COLOR_ATTACHMENT" + i + "_WEBGL"]); // gl_FragData[i]
            }
            ext.drawBuffersWEBGL(buffers);
        }
    }

    public initialize() {
        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();
        let params = this.framebuffer.getParameters();

        // Create Framebuffer
        this.fboID = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboID);

        // Create and attach color textures
        if (WebGLContext.isWebGL2) {
            for (let i = 0; i < params.colorTexture.length; ++i) {
                // Create
                let texture = this.initTex(params.size, params.colorTexture[i].filtering, params.colorTexture[i].dataType);

                // Attach
                gl.framebufferTexture2D(gl.FRAMEBUFFER, gl["COLOR_ATTACHMENT" + i], gl.TEXTURE_2D, texture, 0);

                // Save
                this._colorTexture[i] = texture;
            }
        } else {
            let ext = WebGLContext.getFeatureAsExtension("DRAW_BUFFERS");
            for (let i = 0; i < params.colorTexture.length; ++i) {
                // Create
                let texture = this.initTex(params.size, params.colorTexture[i].filtering, params.colorTexture[i].dataType);

                // Attach
                gl.framebufferTexture2D(gl.FRAMEBUFFER, ext["COLOR_ATTACHMENT" + i + "_WEBGL"], gl.TEXTURE_2D, texture, 0);

                // Save
                this._colorTexture[i] = texture;
            }
        }

        // Create and attac h render buffer
        if (params.createDepthRenderBuffer) {
            this._renderBuffer = gl.createRenderbuffer();
            gl.bindRenderbuffer(gl.RENDERBUFFER, this._renderBuffer);
            gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, params.size[0], params.size[1]);
            gl.bindRenderbuffer(gl.RENDERBUFFER, null);

            MemoryManagement.addRenderBufferMem(params.size[0], params.size[1], 3);

            // Attach
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this._renderBuffer);
        }

        // Check state
        let status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
            logWarning("[Context|InitFBO] FBO-Status: " + status);
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    public release() {
        super.release();

        let params = this.framebuffer.getParameters();

        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();

        if (this.fboID) {
            gl.deleteFramebuffer(this.fboID);
        }

        if (this._renderBuffer) {
            gl.deleteRenderbuffer(this._renderBuffer);
            MemoryManagement.removeRenderBufferMem(params.size[0], params.size[1], 3);
        }

        for (let t = 0; t < this._colorTexture.length; ++t) {
            gl.deleteTexture(this._colorTexture[t]);
            MemoryManagement.removeRenderBufferMem(params.size[0], params.size[1], 4);
        }

        this._renderBuffer = null;
        this._colorTexture = [];
    }

    public initTex(aSize: number[], aFiltering: number, aPixelDataType: number) {
        let gl = this._context.getWebGLRenderingContext();

        let texID = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texID);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, aFiltering);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, aFiltering);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        // @ts-ignore
        if (aPixelDataType === WebGLContext.caps.FLOAT16) {
            // @ts-ignore
            gl.texImage2D(gl.TEXTURE_2D, 0, WebGLContext.isWebGL2 ? gl.RGBA16F : gl.RGBA, aSize[0], aSize[1], 0, gl.RGBA, aPixelDataType, new Uint16Array(aSize[0] * aSize[1] * 4));
        }
        else {
            // @ts-ignore
            gl.texImage2D(gl.TEXTURE_2D, 0, WebGLContext.isWebGL2 ? gl.RGBA32F : gl.RGBA, aSize[0], aSize[1], 0, gl.RGBA, aPixelDataType, new Float32Array(aSize[0] * aSize[1] * 4));
        }

        gl.bindTexture(gl.TEXTURE_2D, null);

        MemoryManagement.addRenderBufferMem(aSize[0], aSize[1], 4);

        return texID;
    }
}
